import React, { useCallback, useEffect, useRef, useState } from 'react';
import BASE_URL from '../../config';
import chatWebSocketService from '../../services/chatWebsocketService';
import './messages.css';

// Customer-facing chat with Mavis staff. Separate from Device Chat (SMS
// with the physical glasses, pages/TextMessages) -- this talks to
// mavis_backend's `chat` app instead, and is where the office console's
// bell icon notification points.
//
// Accessibility is the primary design constraint here, not an afterthought
// -- this portal is used by blind and low-vision customers:
//   - the message thread is a role="log" region with aria-live="polite"
//     and aria-relevant="additions", the standard pattern for a chat log:
//     screen readers announce newly-added messages without needing focus
//     moved there, and "polite" (not "assertive") means an incoming
//     message won't interrupt whatever the user's screen reader is
//     already reading.
//   - every control is a real <button>/<textarea>, never a div with an
//     onClick, so Tab order and Enter/Space activation work with zero
//     extra keyboard-handling code.
//   - the message textarea has a real (visually hidden) <label>, not just
//     a placeholder -- placeholders are not a reliable accessible name.
//   - switching conversations moves focus to the thread heading so a
//     screen reader user gets a clear, predictable announcement of what
//     just changed, instead of focus silently staying put or jumping
//     somewhere unrelated.

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
});

const conversationLabel = (conv, myId) => {
  if (conv.is_group) return conv.name || 'Group conversation';
  if (conv.is_support) return 'Mavis Support';
  const other = conv.participants.find((p) => p.id !== myId);
  return other ? (other.first_name || other.username) : 'Conversation';
};

const formatTime = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const Messages = () => {
  const [myId, setMyId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [starting, setStarting] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  const threadHeadingRef = useRef(null);
  const logRef = useRef(null);

  const loadConversations = useCallback(() => {
    setLoadingConversations(true);
    fetch(`${BASE_URL}/api/chat/conversations/`, { headers: authHeaders() })
      .then((res) => res.json())
      .then((data) => {
        setConversations(data.data || []);
        setLoadingConversations(false);
      })
      .catch(() => setLoadingConversations(false));
  }, []);

  useEffect(() => {
    fetch(`${BASE_URL}/api/user-profile/`, { headers: authHeaders() })
      .then((res) => res.json())
      .then((data) => setMyId(data.id))
      .catch(() => {});

    loadConversations();

    chatWebSocketService.connect();
    const unsubMessage = chatWebSocketService.subscribe('chat_message', (payload) => {
      setActiveId((currentActiveId) => {
        if (payload.conversation_id === currentActiveId) {
          setMessages((prev) => [...prev, payload]);
          setStatusMsg(`New message from ${payload.sender ? (payload.sender.first_name || payload.sender.username) : 'someone'}: ${payload.body}`);
        }
        return currentActiveId;
      });
        loadConversations();
    });

    return () => {
      unsubMessage();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadConversations]);

  const openConversation = useCallback((id) => {
    setActiveId(id);
    setLoadingMessages(true);
    fetch(`${BASE_URL}/api/chat/${id}/messages/`, { headers: authHeaders() })
      .then((res) => res.json())
      .then((data) => {
        setMessages(data.data || []);
        setLoadingMessages(false);
        loadConversations();
        // Move focus to the thread heading so a screen reader announces
        // which conversation is now open, rather than leaving focus on
        // the list item that was just activated.
        setTimeout(() => threadHeadingRef.current && threadHeadingRef.current.focus(), 0);
      })
      .catch(() => setLoadingMessages(false));
  }, [loadConversations]);

  const startSupportChat = () => {
    setStarting(true);
    fetch(`${BASE_URL}/api/chat/start-direct/`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({}),
    })
      .then((res) => res.json())
      .then((data) => {
        setStarting(false);
        loadConversations();
        if (data.data) openConversation(data.data.id);
      })
      .catch(() => setStarting(false));
  };

  const handleSend = (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || !activeId) return;

    setSending(true);
    fetch(`${BASE_URL}/api/chat/${activeId}/messages/`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ body }),
    })
      .then((res) => res.json())
      .then((data) => {
        setSending(false);
        setDraft('');
        if (data.data) setMessages((prev) => [...prev, data.data]);
        loadConversations();
      })
      .catch(() => setSending(false));
  };

  const activeConversation = conversations.find((c) => c.id === activeId);

  return (
    <div className="messages-page">
      <div className="aws-header">
        <div className="header-content">
          <h1 className="aws-title">Messages</h1>
          <p className="aws-subtitle">Chat directly with Mavis support.</p>
        </div>
      </div>

      {/* Announces WS-delivered messages that arrive while this page is
          open, for screen readers that may not be focused on the log
          region below -- kept visually hidden, purely an announcement. */}
      <div className="sr-only" aria-live="polite" role="status">{statusMsg}</div>

      <div className="messages-layout">
        <nav className="conversation-list" aria-label="Conversations">
          <button
            className="messages-btn messages-support-btn"
            onClick={startSupportChat}
            disabled={starting}
          >
            {starting ? 'Starting…' : 'Message Support'}
          </button>

          {loadingConversations ? (
            <div className="aws-loading">
              <div className="aws-spinner"></div>
              <p>Loading conversations…</p>
            </div>
          ) : conversations.length === 0 ? (
            <p className="messages-empty">No conversations yet. Use "Message Support" to start one.</p>
          ) : (
            <ul className="conversation-items">
              {conversations.map((c) => (
                <li key={c.id}>
                  <button
                    className={`conversation-item ${activeId === c.id ? 'active' : ''}`}
                    onClick={() => openConversation(c.id)}
                    aria-current={activeId === c.id ? 'true' : undefined}
                  >
                    <span className="conversation-name">{conversationLabel(c, myId)}</span>
                    {c.last_message && (
                      <span className="conversation-preview">{c.last_message.body}</span>
                    )}
                    {c.unread_count > 0 && (
                      <span className="conversation-unread" aria-label={`${c.unread_count} unread messages`}>
                        {c.unread_count}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </nav>

        <section className="message-thread" aria-label="Conversation">
          {!activeConversation ? (
            <p className="messages-empty">Select a conversation, or start a new one with Message Support.</p>
          ) : (
            <>
              <h2 className="thread-heading" tabIndex={-1} ref={threadHeadingRef}>
                {conversationLabel(activeConversation, myId)}
              </h2>

              <div
                className="message-log"
                role="log"
                aria-live="polite"
                aria-relevant="additions"
                ref={logRef}
              >
                {loadingMessages ? (
                  <div className="aws-loading">
                    <div className="aws-spinner"></div>
                    <p>Loading messages…</p>
                  </div>
                ) : messages.length === 0 ? (
                  <p className="messages-empty">No messages yet -- say hello.</p>
                ) : (
                  messages.map((m) => (
                    <div
                      key={m.id}
                      className={`message-bubble ${m.sender && m.sender.id === myId ? 'mine' : 'theirs'}`}
                    >
                      <span className="message-sender">
                        {m.sender ? (m.sender.first_name || m.sender.username) : 'Unknown'}
                      </span>
                      <p className="message-body">{m.body}</p>
                      <span className="message-time">{formatTime(m.created_at)}</span>
                    </div>
                  ))
                )}
              </div>

              <form className="message-composer" onSubmit={handleSend}>
                <label htmlFor="message-draft" className="sr-only">Type a message</label>
                <textarea
                  id="message-draft"
                  className="messages-textarea"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type a message…"
                  rows={2}
                  disabled={sending}
                />
                <button type="submit" className="messages-btn" disabled={sending || !draft.trim()}>
                  {sending ? 'Sending…' : 'Send'}
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default Messages;
